import type { TableColumnsType } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { fn } from '@app/utils';
import type {
    BillingCategories,
    BillingReportRow,
    BillingReportTableRow,
    CategoryTotals,
    CurrencyTotals,
} from '@app/services/api.types';

const calculateGrandTotals = (categoryTotals: CategoryTotals): CurrencyTotals => {
    const grandTotals: CurrencyTotals = {};
    categoryTotals.forEach((total) => {
        Object.entries(total).forEach(([currency, amount]) => {
            if (!grandTotals[currency]) {
                grandTotals[currency] = '0';
            }
            grandTotals[currency] = (Number(grandTotals[currency]) + Number(amount)).toFixed(2);
        });
    });
    return grandTotals;
};

const updateBillingCategories = (billingCategories: BillingCategories, row: BillingReportRow) => {
    const { parent, category, channel_partner, currency, amount, unit } = row;
    const categoryKey = parent.toLowerCase();
    const key = `${parent}-${category}-${channel_partner}-${currency}`;

    if (!billingCategories.has(categoryKey)) {
        billingCategories.set(categoryKey, []);
    }
    const billingCategory = billingCategories.get(categoryKey)!;
    const existingRow = billingCategory.find((billingCategory) => billingCategory.key === key);

    if (existingRow) {
        if (existingRow.unit !== unit) {
            existingRow.unit = 'various';
            existingRow.amount = 'N/A';
        }
        if (existingRow.unit !== 'N/A' && existingRow.unit !== 'various') {
            existingRow.amount = (Number(existingRow.amount) + Number(amount)).toFixed(2);
        }
        existingRow.total = (Number(existingRow.total) + Number(row.total)).toFixed(2);
    } else {
        const newRow = { key: key, ...row };
        if (newRow.unit === 'N/A') {
            newRow.amount = 'N/A';
        }
        billingCategory.push(newRow);
    }
};

const updateCategoryTotals = (categoryTotals: CategoryTotals, row: BillingReportRow) => {
    const { parent, currency, total } = row;
    const categoryKey = parent.toLowerCase();
    if (!categoryTotals.has(categoryKey)) {
        categoryTotals.set(categoryKey, {});
    }
    const categoryTotal = categoryTotals.get(categoryKey)!;
    if (!categoryTotal[currency]) {
        categoryTotal[currency] = '0';
    }
    categoryTotal[currency] = (Number(categoryTotal[currency]) + Number(total)).toFixed(2);
};

const addHeaderAndTotalToBillingCategories = (
    categoryKey: string,
    categoryRows: BillingReportTableRow[],
    totals: CurrencyTotals
) => {
    const capitalisedKey = fn.capitaliseWord(categoryKey);
    const headerRow = {
        key: `${capitalisedKey}-header`,
        category: capitalisedKey,
    };
    const sortedGroup = categoryRows.sort((a, b) => a.key.localeCompare(b.key));
    const subtotalRow = {
        key: `${capitalisedKey}-subtotal`,
        unit: 'Subtotal:',
        ...totals,
    };
    return [headerRow, ...sortedGroup, subtotalRow];
};

export const analyseBillingData = (
    billingData: BillingReportRow[]
): {
    tableData: BillingReportTableRow[];
    grandTotals: CurrencyTotals;
    currencies: string[];
} => {
    if (!billingData?.length) {
        return { tableData: [], grandTotals: {}, currencies: [] };
    }

    const currenciesSet = new Set<string>();
    const categoryTotals = new Map<string, CurrencyTotals>();
    const billingCategories: BillingCategories = new Map();

    billingData.forEach((row) => {
        const { currency } = row;
        currenciesSet.add(currency);
        updateBillingCategories(billingCategories, row);
        updateCategoryTotals(categoryTotals, row);
    });

    const billingGroupsArray = Array.from(billingCategories, ([key, value]) => {
        const totals = categoryTotals.get(key) || {};
        return addHeaderAndTotalToBillingCategories(key, value, totals);
    });

    const grandTotals = calculateGrandTotals(categoryTotals);
    const grandTotalRow = {
        key: 'Total-total',
        unit: 'Total:',
        ...grandTotals,
    };

    return {
        tableData: [...billingGroupsArray.flat(), grandTotalRow],
        grandTotals: grandTotals,
        currencies: Array.from(currenciesSet).sort((a, b) => a.localeCompare(b)),
    };
};

export const createBillingTableCols = (
    baseColumns: TableColumnsType<BillingReportTableRow>,
    currencies: string[]
) => {
    const currencyColumns: ColumnType<BillingReportTableRow>[] = currencies.map((currency) => ({
        title: currency,
        dataIndex: 'total',
        key: 'total',
        render: (value: string, row: BillingReportTableRow) => {
            if (row.currency === currency) {
                return value;
            } else if (row.key.split('-')[1] === 'total' || row.key.split('-')[1] === 'subtotal') {
                return row[currency];
            } else if (row?.key?.split('-')[1] === 'header') {
                return '';
            } else {
                return '-';
            }
        },
        fixed: 'right',
    }));
    return [...baseColumns, ...currencyColumns];
};
